import React from 'react';
import { getArrayIndexFromPathName } from 'platform/forms-system/src/js/patterns/array-builder/helpers';
import {
  titleUI,
  withAlertOrDescription,
} from 'platform/forms-system/src/js/web-component-patterns';

import Autocomplete from '../../../components/AutocompleteNew';
import { ARRAY_PATH, NULL_CONDITION_STRING } from '../../../constants';
import { conditionOptions } from '../../../content/conditionOptions';
import { NewConditionDescription } from '../../../content/conditions';
import { isDuplicateConditionName } from '../../../utils/duplicate-condition-names';
import {
  arrayOptions,
  createAddAndEditTitles,
  isNewCondition,
  isRatedDisability,
} from './utils';

const ERR_TOO_LONG = 'This needs to be less than 256 characters';
// Covers both kinds of duplicate: another new condition, or a rated disability
// claimed for an increase.
const ERR_DUP_CONDITION = 'You’ve already added this condition to your claim';
const ERR_MISSING_CONDITION =
  'Enter a condition, diagnosis, or short description of your symptoms';

const validatePresenceAndLength = (err, text = '') => {
  const trimmed = text.trim();

  if (
    !trimmed ||
    trimmed.toLowerCase() === NULL_CONDITION_STRING.toLowerCase()
  ) {
    err.addError(ERR_MISSING_CONDITION);
    return;
  }

  if (trimmed.length > 255) {
    err.addError(ERR_TOO_LONG);
  }
};

// Maps an itemFilter-filtered array position back to the row's real index in
// the full array.
const resolveRealIndex = (formData, filteredIndex) => {
  const rows = formData?.[ARRAY_PATH] || [];
  let count = -1;

  for (let i = 0; i < rows.length; i += 1) {
    if (!isRatedDisability(formData, i)) {
      count += 1;
      if (count === filteredIndex) {
        return i;
      }
    }
  }

  return filteredIndex;
};

// ui:validations gets a null index on an array-builder item page: the page
// schema is the item itself, so the platform never sets currentIndex. Falling
// through to row 0 would skip the checks below, so read the row from the URL.
// For the review-and-submit page, the URL has no index number, so `index`
// needs to be resolved using the itemFilter-filtered array position.
const resolveIndex = (index, formData) => {
  const urlIndex = getArrayIndexFromPathName();

  if (Number.isInteger(urlIndex)) {
    return urlIndex;
  }

  return Number.isInteger(index) ? resolveRealIndex(formData, index) : index;
};

const validateCondition = (
  errors,
  fieldData = '',
  formData = {},
  _schema,
  _errorMessages,
  index,
) => {
  const currentIndex = resolveIndex(index, formData);

  if (!isNewCondition(formData, currentIndex)) {
    return;
  }

  validatePresenceAndLength(errors, fieldData);

  if (isDuplicateConditionName(formData, currentIndex, fieldData)) {
    errors.addError(`${ERR_DUP_CONDITION}: ${fieldData}`);
  }
};

/** @returns {PageSchema} */
const newConditionPage = {
  uiSchema: {
    ...titleUI(
      () => createAddAndEditTitles('Add new condition', 'Edit new condition'),
      withAlertOrDescription({
        nounSingular: arrayOptions.nounSingular,
      }),
    ),
    condition: {
      'ui:description': NewConditionDescription,
      'ui:field': data => (
        <Autocomplete
          availableResults={conditionOptions}
          debounceDelay={200}
          id={data.idSchema.$id}
          label="Select or enter condition"
          hint="Start typing for a list of conditions."
          formData={data.formData}
          onChange={data.onChange}
        />
      ),
      'ui:errorMessages': {
        required: ERR_MISSING_CONDITION,
        minLength: ERR_MISSING_CONDITION,
      },
      'ui:validations': [validateCondition],
      'ui:options': {
        useAllFormData: true,
        hideLabelText: true,
      },
    },
  },
  schema: {
    type: 'object',
    required: ['condition'],
    properties: {
      condition: {
        type: 'string',
        minLength: 1,
        maxLength: 255,
      },
    },
  },
};

export default newConditionPage;
